/// <amd-module name="Wit/Common/WitSettingsViews/Components/FieldSettingsView" />
import * as React from "react";
import { IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format, newGuid } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { Components, css, usePageContext } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { Header, TitleSize } from "VSSUI/Header";
import { BoltListDragEvent, IListDropData, ListDragDropBehavior, ListDragImage } from "VSSUI/List";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { ITableColumn, renderGripper, SimpleTableCell, Table, TableCell } from "VSSUI/Table";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { FieldSettingsConstants } from "Wit/Common/WitSettingsViews/Components/FieldSettingsConstants";
import * as Resources from "Wit/Common/WitSettingsViews/Resources";
import { getListBoxItemsFromFields, getSupportedFieldDefinitions } from "Wit/Common/WitSettingsViews/Utils/FieldUtils";
import { IFieldReferenceSetting, IFieldSettingsViewProps } from "Wit/Common/WitSettingsViewsTypes/Components/FieldSettingsView.props";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";

/**
 * Customizable field settings view that allows for a list of core fields than can be selected,
 * then configurable additional fields that can be chosen from a supplied list.
 */
export const FieldSettingsView: React.FunctionComponent<IFieldSettingsViewProps> = ({
    additionalFields,
    className,
    coreFields,
    description,
    disabled,
    errorMessage,
    excludedFieldReferenceNames = FieldSettingsConstants.notSupportedFieldReferenceNames,
    labels,
    maxAdditionalFields = 10,
    onSettingsChanged,
    supportedFieldTypes = FieldSettingsConstants.supportedFieldTypes,
    workItemTypeFields
}) => {
    const pageContext = usePageContext();
    let fieldService : IWorkItemFieldService | undefined;
    let fieldService2 : IWorkItemFieldService2 | undefined;
    const useNewFieldService = isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false);
    if (useNewFieldService) {
        fieldService2 = pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
    } else {
        fieldService = pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
    }
    const [loading, setLoading] = React.useState(false);
    const dataProviderErrorMessage = new ObservableValue<string | undefined>(undefined);

    // field dropdown choices and currently selected options
    const fieldOptions = useNewFieldService 
                ? React.useRef(new ObservableArray<IListBoxItem>([])).current 
                : new ObservableArray<IListBoxItem>([]);

    // mirror field references with metadata about table row
    const selections = React.useRef<DropdownSelection[]>(additionalFields.value.map(() => new DropdownSelection()));

    const moveRow = (setting: IFieldReferenceSetting, dropIndex: number) => {
        // First we remove the item that is being dragged, then we add it in a second operation
        const draggedRowIndex = additionalFields.value.findIndex(f => f.id === setting.id);

        // update setting list
        additionalFields.splice(draggedRowIndex, 1);
        additionalFields.splice(dropIndex, 0, setting);

        // update dropdown selection list
        const selection = selections.current.splice(draggedRowIndex, 1)[0];
        selections.current.splice(dropIndex, 0, selection);

        onSettingsChanged({ additionalFields, coreFields });
    };

    const onAddField = () => {
        const defaultSelectedIndex = 0;
        const newField = {
            id: newGuid(),
            referenceName: fieldOptions.value[defaultSelectedIndex].id
        };

        const selection = new DropdownSelection();
        selection.select(defaultSelectedIndex);
        selections.current.push(selection);
        additionalFields.push(newField);

        onSettingsChanged({ additionalFields, coreFields });
    };

    const onRemoveField = (rowIndex: number) => {
        selections.current.splice(rowIndex, 1);
        additionalFields.splice(rowIndex, 1);

        onSettingsChanged({ additionalFields, coreFields });
    };

    // drag and drop behavior
    const onDrop = (event: BoltListDragEvent<HTMLElement, IFieldReferenceSetting>, dropData: IListDropData) => {
        const setting = event.detail.dataTransfer.data;

        moveRow(setting, dropData.index);
    };

    const dragDropBehavior = new ListDragDropBehavior({
        allowedTypes: ["field"],
        onDrop: onDrop,
        renderDragImage: event => {
            const fieldRefName = event.detail.dataTransfer.data?.referenceName ?? "";
            const fieldName = (useNewFieldService 
                ? fieldService2?.getFieldByReferenceName(fieldRefName)?.name 
                : fieldService?.getFieldByReferenceName(fieldRefName)?.name) ?? "";
            return <ListDragImage text={fieldName} />;
        },
        type: "field"
    });

    const renderFieldColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IFieldReferenceSetting>,
        tableItem: IFieldReferenceSetting
    ): JSX.Element => {
        const selection = selections.current[rowIndex];
        const onSelect = (event: any, item: IListBoxItem) => {
            additionalFields.value[rowIndex].referenceName = item.id;
            onSettingsChanged({ additionalFields, coreFields });
        };

        return (
            <SimpleTableCell columnIndex={columnIndex} key={tableItem.id + "-col-" + columnIndex} tableColumn={tableColumn}>
                <Dropdown
                    className="wit-settings-table-dropdown"
                    disabled={disabled}
                    filterPlaceholderText={Resources.DropdownSearchPlaceholder}
                    items={fieldOptions}
                    loading={loading}
                    placeholder={loading ? Resources.LoadingText : undefined}
                    onSelect={onSelect}
                    selection={selection}
                />
            </SimpleTableCell>
        );
    };

    const renderDeleteColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<IFieldReferenceSetting>,
        tableItem: IFieldReferenceSetting
    ): JSX.Element => {
        return (
            <TableCell
                className="bolt-table-cell-side-action"
                columnIndex={columnIndex}
                key={tableItem.id + "-col-" + columnIndex}
                tableColumn={tableColumn}
            >
                <div className="flex-column flex-center">
                    <Button
                        className="bolt-table-cell-content-reveal padding-horizontal-4"
                        disabled={disabled}
                        iconProps={{ iconName: "Delete" }}
                        onClick={() => onRemoveField(rowIndex)}
                        subtle
                    />
                </div>
            </TableCell>
        );
    };

    const columns = [
        {
            id: "field",
            renderCell: renderFieldColumn,
            width: new ObservableValue(-100)
        },
        {
            id: "delete",
            renderCell: renderDeleteColumn,
            width: new ObservableValue(40)
        }
    ];

    const setCheckboxObservable = (observable: IObservableValue<boolean>, checked: boolean) => {
        observable.value = checked;
        onSettingsChanged({ additionalFields, coreFields });
    };

    React.useEffect(() => {
        const getFields = async () => {
            let fields = fieldService?.getFields();
            if (!fields) {
                setLoading(true);
                if (useNewFieldService) {
                    fields = await fieldService2?.loadFields();
                } else {
                    fields = await fieldService?.getFieldsAsync();
                }
            }

            if (fields) {
                if (workItemTypeFields) {
                    const fieldsSet = new Set(workItemTypeFields);
                    fields = fields.filter(field => fieldsSet.has(field.id));
                }

                const supportedFields = getSupportedFieldDefinitions(fields, supportedFieldTypes, excludedFieldReferenceNames);
                fieldOptions.value = getListBoxItemsFromFields(supportedFields);
                additionalFields.value.forEach((fieldRef, index) => {
                    const selection = selections.current[index];
                    const selectionIndex = fieldOptions.value.findIndex(f => f.id === fieldRef.referenceName);
                    selection.select(selectionIndex);
                });
            } else {
                dataProviderErrorMessage.value = useNewFieldService
                        ? fieldService2?.getFieldsErrorMessage()
                        : fieldService?.getFieldsErrorMessage();
            }

            setLoading(false);
        };

        getFields();
    }, [workItemTypeFields]);

    if (loading) {
        return (
            <div className="flex-column flex-grow flex-center justify-center">
                <Spinner size={SpinnerSize.large} />
            </div>
        );
    }

    return (
        <div className={css("flex-column flex-grow rhythm-vertical-24", className)}>
            <div className="padding-horizontal-20 padding-bottom-16">
                <Observer errorMessage={dataProviderErrorMessage}>
                    {(props: { errorMessage: string | undefined }) =>
                        props.errorMessage ? <MessageCard severity={MessageCardSeverity.Error}>{props.errorMessage}</MessageCard> : null
                    }
                </Observer>
            </div>
            <Header className="padding-vertical-0" description={description} title={Resources.FieldSettingsHeader} titleSize={TitleSize.Small} />
            <div className="flex-column padding-horizontal-20 rhythm-vertical-8">
                <span className="font-weight-semibold">{Resources.CoreFieldLabel}</span>
                {Object.keys(coreFields).map(fieldKey => {
                    const observable = coreFields[fieldKey];
                    let label = labels[fieldKey];
                    if (!label) {
                        const fieldName = useNewFieldService 
                            ? fieldService2?.getFieldByReferenceName(fieldKey)?.name
                            : fieldService?.getFieldByReferenceName(fieldKey)?.name;
                        label = fieldName ? format(Resources.ShowFieldLabel, fieldName) : fieldKey;
                    }

                    return (
                        <Checkbox
                            checked={observable}
                            disabled={disabled}
                            key={fieldKey}
                            label={label}
                            onChange={(event: any, checked: boolean) => setCheckboxObservable(observable, checked)}
                        />
                    );
                })}
            </div>
            <div className="flex-column">
                <div className="flex-column padding-horizontal-20 rhythm-vertical-8">
                    <span className="font-weight-semibold">{Resources.AdditionalFieldsLabel}</span>
                    <span className="secondary-text">{Resources.AdditionalFieldsDescription}</span>
                    <Observer errorMessage={errorMessage}>
                        {(observerProps: { errorMessage: string | undefined }) =>
                            observerProps.errorMessage ? (
                                <div className="flex-row flex-center rhythm-horizontal-8">
                                    <Status {...Statuses.Failed} key="error" size={StatusSize.m} />
                                    <span className="error-text">{observerProps.errorMessage}</span>
                                </div>
                            ) : null
                        }
                    </Observer>
                </div>
                <Table
                    ariaLabel={Resources.AdditionalFieldsLabel}
                    behaviors={disabled ? undefined : [dragDropBehavior]}
                    className="padding-vertical-8"
                    columns={columns}
                    itemProvider={additionalFields}
                    renderSpacer={disabled ? undefined : renderGripper}
                    showHeader={false}
                    showLines={false}
                />
                {!disabled && (
                    <div className="padding-horizontal-20 margin-top-8">
                        <Observer additionalFields={additionalFields}>
                            {() => (
                                <Button
                                    disabled={additionalFields.length >= maxAdditionalFields}
                                    iconProps={{ iconName: "Add" }}
                                    onClick={onAddField}
                                    text={Resources.AddFieldButtonLabel}
                                />
                            )}
                        </Observer>
                    </div>
                )}
            </div>
        </div>
    );
};

export function validateAdditionalFields(additionalFields: IFieldReferenceSetting[]): string | undefined {
    let errorMessage = undefined;
    const uniqueFields = new Set<string>();
    for (const field of additionalFields) {
        if (uniqueFields.has(field.referenceName)) {
            errorMessage = Resources.DuplicateFieldsErrorMessage;
            break;
        }

        uniqueFields.add(field.referenceName);
    }

    return errorMessage;
}

Components.add("ms.vss-work-web.field-settings-view", FieldSettingsView);
